import { isIsolatedCloud } from '@atlaskit/atlassian-context';
import {
	isPostOfficePlacementsGloballyDisabled,
	type PostOfficeContextValue,
	usePostOfficeContext,
	usePostOfficeRoute,
	useStableObject,
} from '@atlassian/post-office-context';
import {
	PerformanceTracking,
	PerformanceTrackingSegment,
	StableProviders,
} from '@post-office/placement-contexts/create-placement-component/providers';
import {
	type ErrorBoundaryAdditionalAnalytics,
	withErrorBoundary,
} from '@post-office/placement-contexts/withErrorBoundary';
import { type FC, memo, type MemoExoticComponent, type ReactNode } from 'react';

import {
	type ErrorBoundariedPropsV2,
	type PlacementComponentPropsInternalV2,
	type PlacementComponentV2,
} from './types';
import type { PlacementConfigurationOptions } from '../types';
import { type OnPerformanceTrackingEvent } from '@post-office/placement-contexts/create-placement-component/hooks/usePerformanceTracking/types';
import { type ErrorBoundaryPropsWithRender } from 'react-error-boundary';

export const ERROR_BOUNDARY_LOCATION = 'create-placement-component';

export const createBasePlacementComponentV2 = <P,>(
	placementId: string,
	Component: PlacementComponentV2<P>,
	options: PlacementConfigurationOptions | undefined,
): FC<
	Partial<ErrorBoundaryPropsWithRender> &
		ErrorBoundaryAdditionalAnalytics & {
			onEvent?: OnPerformanceTrackingEvent;
			context?: PostOfficeContextValue;
			initialData?: Promise<Response | undefined> | undefined;
		} & P
> =>
	withErrorBoundary<PlacementComponentPropsInternalV2<P>>(
		memo((props) => {
			const providedContext = usePostOfficeContext();
			const route = usePostOfficeRoute();
			const stableContext = useStableObject(props?.context ?? providedContext);

			// Hide placement in Isolated Cloud environments if configured
			// The typeof document check ensures we don't call isIsolatedCloud() during SSR
			if (options?.hideInIsolatedCloud && typeof document !== 'undefined' && isIsolatedCloud()) {
				return null;
			}

			const { onEvent, initialData, ...componentProps } = props;

			const context = props?.context ?? providedContext;

			if (isPostOfficePlacementsGloballyDisabled(context)) {
				return null;
			}

			return (
				<PerformanceTrackingSegment placementId={placementId}>
					<PerformanceTracking initialData={initialData} onEvent={onEvent}>
						<StableProviders
							route={route}
							context={stableContext}
							placement={{ placementId }}
							choreographer={options?.choreographer}
							subscription={options?.subscription}
							subscriptionAdapter={options?.subscriptionAdapter}
						>
							{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
							<Component initialData={initialData} {...(componentProps as any)} />
						</StableProviders>
					</PerformanceTracking>
				</PerformanceTrackingSegment>
			);
		}),
	);

export const createPlacementComponentV2 = <P,>(
	placementId: string,
	Component: PlacementComponentV2<P>,
	options: PlacementConfigurationOptions | undefined,
): MemoExoticComponent<(props: ErrorBoundariedPropsV2<P>) => ReactNode> => {
	const placement = createBasePlacementComponentV2(placementId, Component, options);

	return memo((props: ErrorBoundariedPropsV2<P>) => {
		return placement({
			...props,
			errorBoundaryLocation: ERROR_BOUNDARY_LOCATION,
			placementId,
			fallbackRender: () => null,
		});
	});
};
